import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * /data-residency — 主要交付物（v2 typography pass）。
 *
 * 對象：潛在買斷客戶（加拿大、非技術背景），不是 end user。
 * 訴求：教育「為什麼方案 A 是 default、什麼時候要升級到 B」。
 *
 * v2 改動（KEEP visual + typography update only）：
 *   - 內容文案不動（policy + compliance 表）
 *   - kicker / 章標走新 var(--font-mono) / var(--font-display) tokens
 *   - hero h1 用 var(--h1-tracking)
 *   - 鎖 minimal theme 不變（trust-first）
 *   - button 視覺改吃 var(--btn-bg) etc.
 */

const COMPLIANCE_KEYS = ['0', '1', '2'] as const;

export default function DataResidency() {
  const { t, i18n } = useTranslation();

  // 鎖 minimal theme：最 trust 的視覺，避免 mystic 暗黑配色搶走 legal 文件的嚴肅感。
  // unmount 時還原成使用者實際選的（從 localStorage 讀）。
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.getAttribute('data-theme');
    html.setAttribute('data-theme', 'minimal');
    return () => {
      const stored = localStorage.getItem('tarot.theme');
      html.setAttribute('data-theme', stored ?? previous ?? 'minimal');
    };
  }, []);

  // 同步 <title> + <meta description> + <html lang>，符合 SEO + a11y
  useEffect(() => {
    document.title = t('dataResidency.meta.title');
    const meta =
      document.querySelector<HTMLMetaElement>('meta[name="description"]') ??
      Object.assign(document.createElement('meta'), { name: 'description' });
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', t('dataResidency.meta.description'));
    if (!meta.parentElement) document.head.appendChild(meta);
    document.documentElement.setAttribute('lang', i18n.resolvedLanguage ?? 'en');
  }, [t, i18n.resolvedLanguage]);

  const fitItems = t('dataResidency.fitTable.fit', { returnObjects: true }) as string[];
  const notFitItems = t('dataResidency.fitTable.notFit', { returnObjects: true }) as string[];
  const compareRows = t('dataResidency.compare.rows', { returnObjects: true }) as Array<{
    criterion: string;
    planA: string;
    planB: string;
  }>;

  return (
    <main className="mx-auto w-full max-w-3xl px-6 pb-24 pt-24 sm:pt-32">
      <Link to="/" className="mb-12 inline-block text-sm text-muted hover:text-fg">
        ← {t('common.nav.back')}
      </Link>

      {/* Hero */}
      <header className="mb-16">
        <p className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-accent">
          {t('dataResidency.hero.kicker')}
        </p>
        <h1
          className="text-balance font-display text-4xl leading-tight text-heading sm:text-5xl"
          style={{ letterSpacing: 'var(--h1-tracking)' }}
        >
          {t('dataResidency.hero.title')}
        </h1>
        <p className="mt-5 text-pretty text-lg leading-relaxed text-muted">
          {t('dataResidency.hero.subtitle')}
        </p>
      </header>

      {/* 1. Our choice */}
      <section className="mb-14">
        <h2 className="font-display text-2xl text-heading">
          {t('dataResidency.choice.heading')}
        </h2>
        <p className="mt-4 text-pretty leading-relaxed text-muted">
          {t('dataResidency.choice.body')}
        </p>
      </section>

      {/* 2. Compliance — Law 25 §14 / §17 / §3.3 */}
      <section className="mb-14">
        <h2 className="font-display text-2xl text-heading">
          {t('dataResidency.compliance.heading')}
        </h2>
        <p className="mt-4 text-pretty leading-relaxed text-muted">
          {t('dataResidency.compliance.intro')}
        </p>
        <ol className="mt-6 space-y-5">
          {COMPLIANCE_KEYS.map((idx) => (
            <li
              key={idx}
              className="border border-border bg-surface p-5"
              style={{ borderRadius: 'var(--radius-card)' }}
            >
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="font-display text-lg text-accent">
                  {t(`dataResidency.compliance.items.${idx}.ref`)}
                </span>
                <h3 className="font-display text-base text-heading">
                  {t(`dataResidency.compliance.items.${idx}.title`)}
                </h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted">
                {t(`dataResidency.compliance.items.${idx}.body`)}
              </p>
            </li>
          ))}
        </ol>
      </section>

      {/* 3. Fit / not-fit */}
      <section className="mb-14">
        <h2 className="font-display text-2xl text-heading">
          {t('dataResidency.fitTable.heading')}
        </h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div
            className="border border-border bg-surface p-5"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            <h3 className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-accent">
              {t('dataResidency.fitTable.fitHeader')}
            </h3>
            <ul className="space-y-2 text-sm leading-relaxed">
              {fitItems.map((item) => (
                <li key={item} className="flex gap-2">
                  <span
                    aria-hidden
                    className="mt-1.5 h-1 w-1 shrink-0 rounded-full"
                    style={{ background: 'var(--color-accent)' }}
                  />
                  <span className="text-pretty">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            className="border border-border p-5"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            <h3 className="mb-3 font-mono text-[0.65rem] uppercase tracking-[0.18em] text-muted">
              {t('dataResidency.fitTable.notFitHeader')}
            </h3>
            <ul className="space-y-2 text-sm leading-relaxed text-muted">
              {notFitItems.map((item) => (
                <li key={item} className="flex gap-2">
                  <span aria-hidden>—</span>
                  <span className="text-pretty">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 4. Compare table */}
      <section className="mb-14">
        <h2 className="font-display text-2xl text-heading">
          {t('dataResidency.compare.heading')}
        </h2>
        <div
          className="mt-6 overflow-x-auto border border-border"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-elevated">
              <tr>
                <th className="px-4 py-3 font-display text-base text-heading">
                  {t('dataResidency.compare.headers.criterion')}
                </th>
                <th className="px-4 py-3 font-display text-base text-heading">
                  {t('dataResidency.compare.headers.planA')}
                </th>
                <th className="px-4 py-3 font-display text-base text-heading">
                  {t('dataResidency.compare.headers.planB')}
                </th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map((row) => (
                <tr key={row.criterion} className="border-t border-border align-top">
                  <td className="px-4 py-3 font-semibold text-fg">{row.criterion}</td>
                  <td className="px-4 py-3 text-muted">{row.planA}</td>
                  <td className="px-4 py-3 text-muted">{row.planB}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 5. Upgrade path */}
      <section
        className="mb-14 border border-border bg-surface p-6"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <h2 className="font-display text-2xl text-heading">
          {t('dataResidency.upgrade.heading')}
        </h2>
        <p className="mt-4 text-pretty leading-relaxed text-muted">
          {t('dataResidency.upgrade.body')}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-accent">
          {t('dataResidency.upgrade.scope')}
        </p>
      </section>

      {/* 6. CTA */}
      <section className="border-t border-border pt-10">
        <h2 className="font-display text-2xl text-heading">{t('dataResidency.cta.heading')}</h2>
        <p className="mt-3 text-pretty leading-relaxed text-muted">
          {t('dataResidency.cta.body')}
        </p>
        <a
          href="mailto:wtxg@hourjungle.com?subject=Tarot%20Chat%20%E2%80%94%20data%20residency%20review"
          className="mt-6 inline-block px-6 py-2.5 text-sm font-medium transition-opacity hover:opacity-90"
          style={{
            background: 'var(--btn-bg)',
            color: 'var(--btn-ink)',
            border: '1px solid var(--btn-border, transparent)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--btn-shadow)',
          }}
        >
          {t('common.cta.bookCall')}
        </a>
      </section>
    </main>
  );
}
